import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ApiKeyScope } from '../types/apiKey';
import { OAuthConsentDecisionResponse, OAuthConsentView } from '../types/oauth';

export type OAuthConsentLoadError = 'not_found' | 'expired' | 'generic';

interface UseOAuthConsentResult {
  consent: OAuthConsentView | null;
  isLoading: boolean;
  error: OAuthConsentLoadError | null;
  errorMessage: string | null;
  /** Approve and return the URL to send the browser back to the client. */
  approve: (organizationId: string, scopes: ApiKeyScope[]) => Promise<string>;
  /** Deny and return the URL to send the browser back to the client. */
  deny: () => Promise<string>;
}

const extractMessage = (err: any, fallback: string): string => {
  const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || fallback;
  return typeof message === 'string' ? message : fallback;
};

/**
 * Loads a pending OAuth authorization request (created by `/oauth/authorize`) for the
 * consent page, and exposes the approve/deny decisions.
 */
export function useOAuthConsent(requestId?: string | null): UseOAuthConsentResult {
  const api = useAuthenticatedApi();

  const [consent, setConsent] = useState<OAuthConsentView | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(requestId));
  const [error, setError] = useState<OAuthConsentLoadError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!requestId) {
      setConsent(null);
      setError('not_found');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setErrorMessage(null);
      setConsent(await api.get<OAuthConsentView>(apiEndpoints.oauthConsent.get(requestId)));
    } catch (err: any) {
      setConsent(null);
      if (err?.status === 404) {
        setError('not_found');
      } else if (err?.status === 400) {
        setError('expired');
      } else {
        setError('generic');
        setErrorMessage(extractMessage(err, 'Failed to load the connection request'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, requestId]);

  const approve = useCallback(
    async (organizationId: string, scopes: ApiKeyScope[]): Promise<string> => {
      if (!requestId) throw new Error('No connection request');
      const response = await api.post<OAuthConsentDecisionResponse>(
        apiEndpoints.oauthConsent.approve(requestId),
        { organization_id: organizationId, scopes }
      );
      return response.redirect_url;
    },
    [api, requestId]
  );

  const deny = useCallback(async (): Promise<string> => {
    if (!requestId) throw new Error('No connection request');
    const response = await api.post<OAuthConsentDecisionResponse>(apiEndpoints.oauthConsent.deny(requestId));
    return response.redirect_url;
  }, [api, requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { consent, isLoading, error, errorMessage, approve, deny };
}
